import Notification from 'components/notification/Notification';
import PageHeader from 'components/common/PageHeader';
import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Row, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import {
  fetchNotificationsFeed,
  markNotificationsRead,
  subscribeIncomingNotifications,
  syncNotifications,
} from 'helpers/notifications';

const CATEGORY_KEYS = [
  'mercadolibre',
  'mercadolibre_communications',
  'internal',
  'sales',
  'questions'
];

const formatNotificationTime = value => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
};

const escapeHtml = value =>
  String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const buildNotificationMarkup = notification => {
  const title = escapeHtml(notification.title || '');
  const message = escapeHtml(notification.message || '');
  const metadata = notification.metadata || {};
  const actions = Array.isArray(metadata?.actions) ? metadata.actions : [];
  const categoryName = escapeHtml(metadata?.category_name || '');
  const subCategory = escapeHtml(metadata?.sub_category || '');
  const highlighted = Boolean(metadata?.highlighted);
  const tags = Array.isArray(metadata?.tags) ? metadata.tags.filter(Boolean) : [];
  const imageUrl = metadata.publication_image_url || '';
  
  const metaParts = [categoryName, subCategory].filter(Boolean);
  const metaMarkup = metaParts.length
    ? `<div class="text-primary fs--2 mt-1">${metaParts.join(' · ')}</div>`
    : '';
  const tagsMarkup = tags.length
    ? `<div class="fs--2 mt-2 d-flex flex-wrap gap-1">${tags
        .slice(0, 4)
        .map(tag => `<span class="badge badge-soft-info me-1">${escapeHtml(tag)}</span>`)
        .join('')}</div>`
    : '';
  const highlightedMarkup = highlighted
    ? `<div class="fs--2 mt-1 text-warning"><strong>Destacado</strong></div>`
    : '';
  const imageMarkup = imageUrl && metadata.item_id
    ? `<div class="mt-2 mb-2"><img src="${escapeHtml(imageUrl)}" alt="${escapeHtml(metadata.publication_title || '')}" style="max-width: 100%; height: auto; border-radius: 4px; max-height: 150px;" /></div>`
    : '';
  const publicationTitleMarkup = metadata.publication_title && metadata.item_id && !message.includes(metadata.publication_title)
    ? `<div class="fs--2 text-500 mt-1"><em>${escapeHtml(metadata.publication_title)}</em></div>`
    : '';
  const actionsMarkup = actions.length
    ? `<div class="fs--2 mt-2">${actions
        .map(action => {
          const text = escapeHtml(action?.text || '');
          const link = escapeHtml(action?.link || '');
          if (!text || !link) return '';
          return `<a href="${link}" target="_blank" rel="noreferrer noopener">${text}</a>`;
        })
        .filter(Boolean)
        .join(' · ')}</div>`
    : '';
  const baseMarkup = `<strong>${title}</strong>${imageMarkup}${publicationTitleMarkup}`;
  if (!message) {
    return `${baseMarkup}${highlightedMarkup}${metaMarkup}${tagsMarkup}${actionsMarkup}`;
  }
  return `${baseMarkup}<div class="text-600 mt-1">${message}</div>${highlightedMarkup}${metaMarkup}${tagsMarkup}${actionsMarkup}`;
};

const Notifications = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState([]);
  const [allowedCategories, setAllowedCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [unreadCount, setUnreadCount] = useState(0);
  const [error, setError] = useState('');
  const [meta, setMeta] = useState({});

  const visibleCategories = useMemo(
    () => CATEGORY_KEYS.filter(category => allowedCategories.includes(category)),
    [allowedCategories]
  );

  const loadNotifications = React.useCallback(async category => {
    setLoading(true);
    setError('');
    try {
      const data = await fetchNotificationsFeed(category);
      setNotifications(data.notifications || []);
      setAllowedCategories(data.allowed_categories || []);
      setUnreadCount(Number(data.unread_count || 0));
      setMeta(currentState => ({
        ...currentState,
        ...(data.meta || {})
      }));
    } catch (requestError) {
      console.error('Error loading notifications:', requestError);
      setError(t('NOTIFICATIONS.ERRORS.LOAD'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    let mounted = true;

    const initialize = async () => {
      await loadNotifications(selectedCategory);
      if (!mounted) {
        return;
      }

      try {
        const data = await syncNotifications(
          selectedCategory ? { category: selectedCategory } : {}
        );
        if (!mounted) {
          return;
        }
        setNotifications(data.notifications || []);
        setAllowedCategories(data.allowed_categories || []);
        setUnreadCount(Number(data.unread_count || 0));
        setMeta(currentState => ({
          ...currentState,
          ...(data.meta || {})
        }));
      } catch (requestError) {
        if (requestError?.response?.status !== 429) {
          console.error('Error syncing notifications in background:', requestError);
        }
      }
    };

    initialize();

    return () => {
      mounted = false;
    };
  }, [loadNotifications, selectedCategory]);

  useEffect(() => {
    const unsubscribe = subscribeIncomingNotifications(incomingNotifications => {
      if (!incomingNotifications.length) {
        return;
      }

      const hasMatchingCategory = incomingNotifications.some(notification => {
        if (!selectedCategory) {
          return true;
        }
        return String(notification?.category || '').toLowerCase() === String(selectedCategory).toLowerCase();
      });

      if (!hasMatchingCategory) {
        return;
      }

      loadNotifications(selectedCategory);
    });

    return unsubscribe;
  }, [loadNotifications, selectedCategory]);

  const handleMarkAllRead = async () => {
    try {
      await markNotificationsRead(selectedCategory ? { category: selectedCategory } : {});
      setNotifications(currentState =>
        currentState.map(notification => ({
          ...notification,
          unread: false
        }))
      );
      setUnreadCount(0);
    } catch (requestError) {
      console.error('Error marking all notifications as read:', requestError);
      setError(t('NOTIFICATIONS.ERRORS.MARK_READ'));
    }
  };

  const formatDateTime = value => {
    if (!value) return t('NOTIFICATIONS.NEVER');
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString();
  };

  const handleNotificationClick = async notification => {
    if (!notification.unread) {
      return;
    }

    try {
      await markNotificationsRead({ notification_ids: [notification.id] });
      setNotifications(currentState =>
        currentState.map(item =>
          item.id === notification.id
            ? { ...item, unread: false }
            : item
        )
      );
      setUnreadCount(currentCount => (currentCount > 0 ? currentCount - 1 : 0));
    } catch (requestError) {
      console.error('Error marking notification as read:', requestError);
    }
  };

  return (
    <>
      <PageHeader
        title={t('NOTIFICATIONS.PAGE_TITLE')}
        description={t('NOTIFICATIONS.PAGE_DESCRIPTION')}
        className="mb-3"
      >
        <div className="mt-3 d-flex flex-wrap gap-2 align-items-center">
          <Badge bg="light" text="dark">
            {t('NOTIFICATIONS.UNREAD_COUNT', { count: unreadCount })}
          </Badge>
          <Badge bg="light" text="dark">
            {t('NOTIFICATIONS.META.LAST_SYNC')}: {formatDateTime(meta.last_synced_at)}
          </Badge>
          <Button variant="outline-primary" size="sm" onClick={handleMarkAllRead}>
            {t('NOTIFICATIONS.ACTIONS.MARK_ALL_READ')}
          </Button>
        </div>
      </PageHeader>

      {error ? <Alert variant="danger">{error}</Alert> : null}

      <Card>
        <Card.Header className="bg-light">
          <Row className="g-2 align-items-center">
            <Col sm={12} md={8}>
              <div className="d-flex flex-wrap gap-2">
                <Button
                  size="sm"
                  variant={selectedCategory ? 'falcon-default' : 'primary'}
                  onClick={() => setSelectedCategory('')}
                >
                  {t('NOTIFICATIONS.CATEGORIES.ALL')}
                </Button>
                {visibleCategories.map(category => (
                  <Button
                    key={category}
                    size="sm"
                    variant={selectedCategory === category ? 'primary' : 'falcon-default'}
                    onClick={() => setSelectedCategory(category)}
                  >
                    {t(`NOTIFICATIONS.CATEGORIES.${category.toUpperCase()}`)}
                  </Button>
                ))}
              </div>
            </Col>
          </Row>
        </Card.Header>
        <Card.Body className="p-0">
          {loading ? (
            <div className="py-5 text-center">
              <Spinner animation="border" size="sm" className="me-2" />
              {t('NOTIFICATIONS.LOADING')}
            </div>
          ) : notifications.length ? (
            notifications.map(notification => (
              <Notification
                key={notification.id}
                unread={notification.unread}
                time={formatNotificationTime(notification.occurred_at || notification.created_at)}
                to={notification.link || '#!'}
                onClick={() => handleNotificationClick(notification)}
              >
                {buildNotificationMarkup(notification)}
              </Notification>
            ))
          ) : (
            <div className="py-5 text-center text-600">{t('NOTIFICATIONS.EMPTY')}</div>
          )}
        </Card.Body>
      </Card>
    </>
  );
};

export default Notifications;
